import { cn } from "@utils/tw"
import { useRouter } from "eddev/routing"
import React, { useEffect, useRef, useState } from "react"
import { useRafLoop, useWindowSize } from "react-use"

export type TabsType = {
  label: string
  href: string
}[]

export const TabBar = ({
  tabs,
  activeTab,
  setActiveTab,
  useHref = false,
  className,
  buttonClassName,
  activeButtonClassName,
  sliderClassName,
  onClick,
  mobileFlat = false,
}: {
  tabs: TabsType
  activeTab: number
  setActiveTab: React.Dispatch<React.SetStateAction<number>> | ((i: number) => void)
  useHref?: boolean
  className?: string
  buttonClassName?: string
  activeButtonClassName?: string
  sliderClassName?: string
  onClick?: () => void
  mobileFlat?: boolean
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { width } = useWindowSize()
  const [w, setW] = useState(0)
  const [x, setX] = useState(0)
  const buttonRefs = tabs.map(() => React.createRef<HTMLButtonElement>())
  const router = useRouter()
  const [hovering, setHovering] = useState(-1)
  const lastCheckTime = useRef(0)
  const hoverOutTimer = useRef<any>()
  const lastActiveTab = useRef(-1)

  const resizeButton = () => {
    const button = buttonRefs[activeTab]?.current
    if (button) {
      setW(button.offsetWidth)
      setX(button.offsetLeft)
    }
  }

  useEffect(() => {
    resizeButton()
  }, [activeTab, width])

  useEffect(() => {
    if (lastActiveTab.current !== -1) {
      const button = buttonRefs[activeTab]?.current
      if (window.innerWidth < 700) {
        button?.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
    lastActiveTab.current = activeTab
  }, [activeTab])

  useRafLoop(() => {
    if (lastCheckTime.current + 500 < Date.now() && (hovering === -1 || window.innerWidth < 700)) {
      resizeButton()
      lastCheckTime.current = Date.now()
    }
  })

  const navClicked = (i: number) => {
    setActiveTab(i)
    onClick && onClick()
    if (useHref) {
      router.navigate(tabs[i].href)
    }
  }

  const hoverOver = (i: number) => {
    clearTimeout(hoverOutTimer.current)
    const button = buttonRefs[i]?.current
    if (button) {
      setW(button.offsetWidth)
      setX(button.offsetLeft)
      setHovering(i)
    }
  }

  const hoverOut = (i: number) => {
    clearTimeout(hoverOutTimer.current)
    hoverOutTimer.current = setTimeout(() => {
      setHovering(-1)
      const button = buttonRefs[activeTab]?.current
      if (button) {
        setW(button.offsetWidth)
        setX(button.offsetLeft)
      }
    }, 200)
  }

  return (
    <div
      ref={ref}
      className={cn(
        "md:h-7 p-[3px] border border-current rounded-md w-full md:w-fit",
        !mobileFlat && "h-buttonLg overflow-auto snap-x snap-mandatory scroll-smooth hide-scrollbars",
        mobileFlat && "h-fit",
        className,
      )}
    >
      <div
        className={cn(
          "flex h-full items-center md:gap-1 relative",
          !mobileFlat ? "flex-nowrap gap-5 justify-start" : "flex-wrap gap-3 justify-center",
        )}
      >
        <div
          className={cn(
            "absolute left-0 top-0 h-full bg-current rounded-md transition-all duration-200",
            hovering >= 0 || activeTab >= 0 ? "opacity-100" : "opacity-0",
            mobileFlat && "hidden md:block",
            sliderClassName,
          )}
          style={{ width: `${w}px`, transform: `translateX(${x}px)` }}
        />
        {tabs.map((tab, i) => (
          <button
            key={tab.label}
            ref={buttonRefs[i]}
            onClick={() => navClicked(i)}
            onMouseOver={() => hoverOver(i)}
            onMouseOut={() => hoverOut(i)}
            className={cn(
              "shrink-0 snap-center relative px-4 md:px-3 rounded-mdu md:rounded-md transition-colors duration-200 flex items-center justify-center text-current type-ui-label leading-none",
              (activeTab === i && hovering === -1) || hovering === i ? "text-offWhite" : "",
              mobileFlat ? "h-9 md:h-full" : "h-full",
              activeTab === i ? activeButtonClassName : buttonClassName,
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  )
}
